import React from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { History } from "history";
import { ConnectedProps, connect } from "react-redux";
import Common from "us.common";
import { ConfigurationProvider } from "us.common/ConfigurationContext";
import commonRoutes from "us.common/routes";
import { SignalRProvider } from "us.common/SignalRProvider";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import { operationsRoutes } from "routes";
import { RootState } from "us.helper/types";
import { FeatureFlagProvider } from "us.common/FeatureFlagContext";
import { useAuth } from "us.helper/hooks/useAuth";
import { Loader, LoginRedirect } from "us.common/components";
import { DrawerProvider } from "us.common/context";

const { Page404, AppLayout, ConfigProvider } = Common.Components;

type AppProps = {
  history: History;
};

const App: React.FC<AppProps & PropsFromRedux> = ({
  history,
  currentLanguage,
}) => {
  const { isAuthenticated, isInitialized } = useAuth();

  if (!isInitialized) {
    return <Loader />;
  }

  if (!isAuthenticated) {
    return (
      <UnauthenticatedTemplate>
        <BrowserRouter
          future={{
            v7_relativeSplatPath: true,
          }}
        >
          <LoginRedirect />
        </BrowserRouter>
      </UnauthenticatedTemplate>
    );
  }

  return (
    <AuthenticatedTemplate>
      <BrowserRouter
        future={{
          v7_relativeSplatPath: true,
        }}
      >
        <ConfigurationProvider>
          <FeatureFlagProvider>
            <DrawerProvider>
              <SignalRProvider>
                <AppLayout enableSearch={false}>
                  <ConfigProvider locale={currentLanguage as any}>
                    <Routes>
                      {[...operationsRoutes(), ...commonRoutes].map(
                        (route, index) =>
                          React.cloneElement(route, { key: index })
                      )}
                      <Route path="*" element={<Page404 />} />
                    </Routes>
                  </ConfigProvider>
                </AppLayout>
              </SignalRProvider>
            </DrawerProvider>
          </FeatureFlagProvider>
        </ConfigurationProvider>
      </BrowserRouter>
    </AuthenticatedTemplate>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentLanguage: state.common.currentLanguage,
  };
};
const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(App);
